import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

interface Company {
  id?: string;
  name: string;
  slug?: string;
  doc_cnpj?: string;
  timezone: string;
  status: string;
  branding?: {
    logo?: string;
    primary_color?: string;
    secondary_color?: string;
  };
}

interface CompanyWithAdmin extends Omit<Company, 'id'> {
  admin_email: string;
  admin_name?: string;
}

interface CompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  onSave: (data: Omit<Company, 'id'> | CompanyWithAdmin) => Promise<void>;
}

const timezones = [
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
  { value: 'America/New_York', label: 'New York (UTC-5)' },
  { value: 'Europe/London', label: 'London (UTC+0)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
];

export function CompanyModal({ open, onOpenChange, company, onSave }: CompanyModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const isCreating = !company;
  
  const [formData, setFormData] = useState<Omit<Company, 'id'>>({
    name: company?.name || '',
    doc_cnpj: company?.doc_cnpj || '',
    timezone: company?.timezone || 'America/Sao_Paulo',
    status: company?.status || 'active',
    branding: {
      logo: company?.branding?.logo || '',
      primary_color: company?.branding?.primary_color || '#2563eb',
      secondary_color: company?.branding?.secondary_color || '#64748b',
    },
  });
  
  const [adminData, setAdminData] = useState({
    admin_email: '',
    admin_name: '',
  });

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: 'Erro',
        description: 'Nome da empresa é obrigatório',
        variant: 'destructive',
      });
      return;
    }

    if (isCreating && !adminData.admin_email.trim()) {
      toast({
        title: 'Erro',
        description: 'Email do administrador é obrigatório',
        variant: 'destructive',
      });
      return;
    }

    if (isCreating && adminData.admin_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminData.admin_email)) {
      toast({
        title: 'Erro',
        description: 'Email do administrador deve ter um formato válido',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      if (isCreating) {
        const dataWithAdmin: CompanyWithAdmin = {
          ...formData,
          slug: generateSlug(formData.name),
          admin_email: adminData.admin_email,
          admin_name: adminData.admin_name || adminData.admin_email,
        };
        console.log('Tentando criar empresa com administrador:', dataWithAdmin);
        await onSave(dataWithAdmin);
      } else {
        console.log('Tentando atualizar empresa:', formData);
        await onSave(formData);
      }
      
      onOpenChange(false);
      toast({
        title: 'Sucesso',
        description: isCreating ? 'Empresa criada com sucesso' : 'Empresa atualizada com sucesso',
      });
    } catch (error: any) {
      console.error('Erro ao salvar empresa:', error);
      
      let errorMessage = 'Erro ao salvar empresa';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error_description) {
        errorMessage = error.error_description;
      } else if (error?.details) {
        errorMessage = error.details;
      }
      
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (key: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateBranding = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      branding: { ...prev.branding, [key]: value }
    }));
  };

  const updateAdminData = (key: keyof typeof adminData, value: string) => {
    setAdminData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {company ? 'Editar Empresa' : 'Nova Empresa'}
          </DialogTitle>
          <DialogDescription>
            {company ? 'Atualize os dados da empresa.' : 'Cadastre uma nova empresa na plataforma.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            {/* Dados da Empresa */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Dados da Empresa</h4>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Nome *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  className="col-span-3"
                  placeholder="Nome da empresa"
                />
              </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="doc_cnpj" className="text-right">
                CNPJ
              </Label>
              <Input
                id="doc_cnpj"
                value={formData.doc_cnpj}
                onChange={(e) => updateFormData('doc_cnpj', e.target.value)}
                className="col-span-3"
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="timezone" className="text-right">
                Fuso Horário
              </Label>
              <Select value={formData.timezone} onValueChange={(value) => updateFormData('timezone', value)}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status
              </Label>
              <Select value={formData.status} onValueChange={(value) => updateFormData('status', value)}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="suspended">Suspenso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="logo" className="text-right">
                Logo URL
              </Label>
              <Input
                id="logo"
                value={formData.branding?.logo}
                onChange={(e) => updateBranding('logo', e.target.value)}
                className="col-span-3"
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="primary_color" className="text-right">
                Cor Primária
              </Label>
              <Input
                id="primary_color"
                type="color"
                value={formData.branding?.primary_color}
                onChange={(e) => updateBranding('primary_color', e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="secondary_color" className="text-right">
                Cor Secundária
              </Label>
              <Input
                id="secondary_color"
                type="color"
                value={formData.branding?.secondary_color}
                onChange={(e) => updateBranding('secondary_color', e.target.value)}
                className="col-span-3"
              />
            </div>
            </div>

            {/* Administrador Inicial - apenas na criação */}
            {isCreating && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium text-muted-foreground">Administrador Inicial</h4>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="admin_email" className="text-right">
                    Email *
                  </Label>
                  <Input
                    id="admin_email"
                    type="email"
                    value={adminData.admin_email}
                    onChange={(e) => updateAdminData('admin_email', e.target.value)}
                    className="col-span-3"
                    placeholder="email@empresa.com"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="admin_name" className="text-right">
                    Nome
                  </Label>
                  <Input
                    id="admin_name"
                    value={adminData.admin_name}
                    onChange={(e) => updateAdminData('admin_name', e.target.value)}
                    className="col-span-3"
                    placeholder="Nome do administrador (opcional)"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}